import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../pageWrapper";

//CSS
import "./userSettingsPage.css";

//Components
import PageLoader from "../../Components/Other Components/pageLoader";
import Title from "../../Components/Other Components/Title";
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import PermissionView from "../../Components/User Components/PermissionView";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import WarehousePicker from "../../Components/Warehouse Components/warehousePicker";

//Functions and hooks
import { useErrorHandler } from "../../hooks/errorHandler";
import { GetStorageLink } from "../../BackendAccess/FrontendHelpers";
import { useNotification } from "../../Contexts/NotificationContext";
import {
  GetUserInfo,
  EditUserInfo,
  DeleteAccount,
  LeaveCompany,
  ChangePassword,
} from "../../BackendAccess/UserHooks";
import { checkPasswordInput } from "../../BackendAccess/FrontendHelpers";
import { GetRoleData } from "../../BackendAccess/RoleHooks";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import useFormState from "../../hooks/useFormStates";
import { usePopup } from "../../Contexts/PopupContext";
import { useTranslation } from "react-i18next";
import useClickOutside from "../../hooks/useClickOff";

function ChangePasswordPopup({ onClose, showNotification }) {
  const { t } = useTranslation();
  const ref = useClickOutside(() => onClose());
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (newPassword !== confirmPassword) {
      showNotification(
        t("New password and confirmation do not match."),
        "error"
      );
      return;
    }
    const passwordCheck = checkPasswordInput(newPassword);
    if (passwordCheck !== true) {
      showNotification(passwordCheck.err, "error");
      return;
    }
    setSubmitting(true);
    const [data, error] = await ChangePassword(currentPassword, newPassword);
    setSubmitting(false);
    if (error) {
      showNotification(error.err, "error");
      return;
    }
    showNotification(t("Password changed successfully."), "success");
    onClose();
  };

  return (
    <div className="popup-overlay">
      <div className="popup-background"></div>
      <div ref={ref} className="popup-container info">
        <div className="popupConfMsg">
          <p>{t("Change Password")}</p>
        </div>
        <InputGroup className="w-100">
          <InputField
            type="password"
            label={t("Current Password") + ":"}
            value={currentPassword}
            onChange={setCurrentPassword}
            className="w-100% mb-2 mt-2"
          />
          <InputField
            type="password"
            label={t("New Password") + ":"}
            value={newPassword}
            onChange={setNewPassword}
            className="w-100% mb-2"
          />
          <InputField
            type="password"
            label={t("Confirm New Password") + ":"}
            value={confirmPassword}
            onChange={setConfirmPassword}
            className="w-100% mb-2"
          />
        </InputGroup>
        <div className="btn-container justify-center">
          <Button
            btnStyle={submitting ? "btn-disabled" : "btn-positive"}
            icon={"apply"}
            onClick={submitting ? () => {} : handleSubmit}
          >
            {t("Apply Changes")}
          </Button>
          <Button btnStyle="btn-primary" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function UserSettingsPage() {
  const [applyDisabled, setApplyDisabled] = useState(false);
  const [removeImageDisabled, setRemoveImageDisabled] = useState(false);
  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);
  const [leaveDisabled, setLeaveDisabled] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { formData, setField, loadInitialData, hasChanged } = useFormState({
    name: "",
    lastName: "",
    username: "",
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [ID, setID] = useState(-1);
  const [role, setRole] = useState("");
  const [roleData, setRoleData] = useState(null);
  const [allowedWarehouses, setAllowedWarehouses] = useState([]);

  const [employerID, setEmployerID] = useState(-1);
  const [employerName, setEmployerName] = useState("");
  const [employerLastName, setEmployerLastName] = useState("");
  const [companyName, setCompanyName] = useState("");

  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const isNarrow = useNarrowMode();

  const { showPopup, removePopup } = usePopup();

  const { t } = useTranslation();

  useErrorHandler(error, showNotification, setPageError);

  const showChangePasswordPopup = () => {
    const key = Date.now();
    showPopup(
      <ChangePasswordPopup
        showNotification={showNotification}
        onClose={() => removePopup(key)}
      />,
      key
    );
  };

  const showDeleteAccountPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to delete your account?", {
              ns: "settings",
            })}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => {
          handleConfirmDelete();
          removePopup(key);
        }}
        onCancel={() => {
          removePopup(key);
        }}
        highSecurity={true}
      />,
      key
    );
  };
  const showLeaveCompanyPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to leave this company?", {
              ns: "settings",
            })}
            <br />
            <span className="text-negative">
              {t("You will only be able to return if you get an invite.", {
                ns: "settings",
              })}
            </span>
          </p>
        }
        onConfirm={() => {
          handleConfirmLeave();
          removePopup(key);
        }}
        onCancel={() => {
          removePopup(key);
        }}
      />,
      key
    );
  };

  useEffect(() => {
    GetUserData();
  }, []);

  const GetUserData = async () => {
    setLoading(true);
    try {
      const [data, error] = await fetchWithTimeout(GetUserInfo);
      if (error) {
        error.isPageError = true;
        setError(error);
        console.log("User data error 1", error);
      } else {
        LoadUserData(data);
        await GetAllowedWarehouses(data.RoleID);
      }
    } catch (err) {
      console.log("User data error 2", err);

      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const LoadUserData = (data) => {
    const imagePath = data.ImagePath ? GetStorageLink() + data.ImagePath : null;

    setEmail(data.Email);
    setID(data.UserID);

    setPreview(imagePath);
    setEmployerID(data.OwnerID);
    setEmployerName(data.OwnerFirstName);
    setEmployerLastName(data.OwnerLastName);
    setCompanyName(data.CompanyName);

    loadInitialData({
      name: data.FirstName,
      lastName: data.LastName,
      username: data.Username,
    });
  };

  const GetAllowedWarehouses = async (roleID) => {
    if (!roleID) {
      setRole("none");
      setAllowedWarehouses([]);
      return;
    }
    try {
      const [data, error] = await fetchWithTimeout(() => GetRoleData(roleID));
      if (error) {
        console.log("User wh data error 1", error);

        error.isPageError = true;
        setError(error);
      } else {
        setRoleData(data);
        setRole(data.Name);
        setAllowedWarehouses(data.AllowedWarehouses);
      }
    } catch (err) {
      console.log("User wh data error 2", err);

      throw new Error(
        t("Error while getting allowed warehouses!", { ns: "employees" })
      );
    }
  };

  const HandleApplyClick = async () => {
    try {
      showNotification(t("Updating user info...", { ns: "settings" }), "info");
      setApplyDisabled(true);
      const removeImage = preview !== null ? false : true;
      const [data, error] = await fetchWithTimeout(() =>
        EditUserInfo(
          formData.name,
          formData.lastName,
          formData.username,
          image,
          removeImage
        )
      );
      if (error) {
        setError(error);
      } else {
        loadInitialData({
          ...(formData.name ? { name: formData.name } : {}),
          ...(formData.lastName ? { lastName: formData.lastName } : {}),
          ...(formData.username ? { username: formData.username } : {}),
        });
        showNotification(
          t("User info updated successfully!", { ns: "settings" }),
          "success"
        );
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setApplyDisabled(false);
    }
  };

  useEffect(() => {
    if (checkIfDifferent()) {
      setApplyDisabled(false);
    } else setApplyDisabled(true);
  }, [formData, preview, image, hasChanged]);

  const checkIfDifferent = () => {
    return image !== null || hasChanged;
  };

  const handleInputChange = (field, value) => {
    setField(field, value);
  };

  const handleImageChange = (file) => {
    setImage(file);
    if (file) {
      setImageLoading(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setImageLoading(false);
      };
      reader.onerror = () => {
        setError({
          err: t("Failed to load image.", { ns: "error" }),
          errCode: 400,
        });
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      setImageLoading(false);
    }
  };

  const handleRemoveImageClick = () => {
    setImage(null);
    setPreview(null);
  };

  const handleConfirmDelete = async () => {
    showNotification(t("Deleting account...", { ns: "settings" }), "info");
    setDeleteBtnDisabled(true);

    try {
      const [data, error] = await fetchWithTimeout(DeleteAccount);
      if (error) {
        console.log("DELETING ACCOUNT1", error);

        setError(error);
      } else {
        console.log("DELETING ACCOUNT2");

        showNotification(
          t("Account deleted successfully!", { ns: "settings" }),
          "success"
        );
        navigate("/login");
      }
    } catch (err) {
      console.log("DELETING ACCOUNT3");

      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setDeleteBtnDisabled(false);
    }
  };

  const handleConfirmLeave = async () => {
    showNotification(t("Leaving company...", { ns: "settings" }), "info");
    setLeaveDisabled(true);

    try {
      const [data, error] = await LeaveCompany();
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("You left the company successfully!", { ns: "settings" }),
          "success"
        );

        navigate("/login");
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLeaveDisabled(false);
    }
  };

  useEffect(() => {
    if (employerID === "/") setLeaveDisabled(true);
    else setLeaveDisabled(false);
  }, [employerID]);

  const tutorialSteps = [
    {
      target: null,
      title: t("Your account", { ns: "tutorial" }),
      message: t(
        "This is your User Settings page - your personal profile, password, permissions, and account-level actions all live here.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-profile",
      title: t("Your profile", { ns: "tutorial" }),
      message: t(
        "Upload a profile picture and edit your username and name. Your ID, email, and role are shown for reference but can't be changed here.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-remove-image-btn",
      title: t("Remove Image", { ns: "tutorial" }),
      message: t("Removes your current profile picture.", { ns: "tutorial" }),
    },
    {
      target: "user-settings-change-password-btn",
      title: t("Change Password", { ns: "tutorial" }),
      message: t(
        "Opens a popup where you can set a new password by entering your current one first.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-apply-btn",
      title: t("Save your changes", { ns: "tutorial" }),
      message: t(
        "Saves any changes you've made to your profile or picture. This button only lights up once something has actually changed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-company",
      title: t("Your company", { ns: "tutorial" }),
      message: t(
        "The company you belong to and who owns it.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-leave-btn",
      title: t("Leave company", { ns: "tutorial" }),
      message: t(
        "Leaves your current company. You'd need a new invite to rejoin. This cannot be undone.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".permission-view",
      title: t("Permissions", { ns: "tutorial" }),
      message: t(
        "Shows everything your current role allows you to do, for your own reference.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-warehouses",
      title: t("Warehouse access", { ns: "tutorial" }),
      message: t(
        "Shows which warehouses you can access, based on your current role.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-delete-btn",
      title: t("Delete account", { ns: "tutorial" }),
      message: t(
        "Permanently deletes your entire account. This cannot be undone, so use it carefully.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "user-settings-package-btn",
      title: t("Change Package", { ns: "tutorial" }),
      message: t(
        "Takes you to your subscription package, where you can see or change what your plan includes.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="userSettings" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">User settings</Title>
          <div
            className={
              "container-bordered mb-2 w-90 " +
              (isNarrow ? "vertical" : "horizontal")
            }
            data-tutorial-id="user-settings-profile"
          >
            <div className="user-settings-image-container">
              <PageLoader loading={imageLoading} error={null}>
                <InputField
                  type={"file"}
                  previewImage={preview}
                  onChange={handleImageChange}
                  label={t("Upload Image")}
                />
              </PageLoader>
            </div>

            <div className="container-empty user-settings-fields vertical">
              <InputField
                className="mb-2 "
                labelPosition={"top-left"}
                label={t("Username") + ":"}
                value={formData.username}
                onChange={(e) => {
                  handleInputChange("username", e);
                }}
              ></InputField>
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                label={t("First Name") + ":"}
                value={formData.name}
                onChange={(e) => {
                  handleInputChange("name", e);
                }}
              ></InputField>

              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                label={t("Last Name") + ":"}
                value={formData.lastName}
                onChange={(e) => {
                  handleInputChange("lastName", e);
                }}
              ></InputField>
            </div>
            <div className="container-empty user-settings-fields vertical">
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                readOnly={true}
                label={t("ID") + ":"}
                value={ID}
              ></InputField>
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                readOnly={true}
                label={t("Email") + ":"}
                value={email}
              ></InputField>
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                readOnly={true}
                label={t("Role") + ":"}
                value={role}
              ></InputField>
            </div>
          </div>

          <div className="btn-container user-settings-outer">
            <div className="btn-container user-settings-inner">
              <Button
                className={isNarrow ? "" : "ml-3"}
                btnStyle={
                  !removeImageDisabled ? "btn-negative" : "btn-disabled"
                }
                icon={"delete"}
                onClick={handleRemoveImageClick}
                tutorialId="user-settings-remove-image-btn"
              >
                {t("Remove Image")}
              </Button>
            </div>
            <Button
              onClick={showChangePasswordPopup}
              className={isNarrow ? "" : ""}
              btnStyle="btn-primary"
              icon={"edit"}
              tutorialId="user-settings-change-password-btn"
            >
              {t("Change Password")}
            </Button>

            <Button
              className={isNarrow ? "mb-2" : "ml-3"}
              btnStyle={!applyDisabled ? "btn-positive" : "btn-disabled"}
              icon={"apply"}
              onClick={HandleApplyClick}
              tutorialId="user-settings-apply-btn"
            >
              {t("Apply Changes")}
            </Button>
          </div>

          <div
            className={
              "container-bordered mt-2 mb-2 w-90 " +
              (isNarrow ? "vertical" : "horizontal")
            }
            data-tutorial-id="user-settings-company"
          >
            <div className="container-empty user-settings-fields vertical">
              <InputField
                className="mb-2"
                labelPosition={"top-left"}
                readOnly={true}
                label={t("Company") + ":"}
                value={companyName}
              ></InputField>
            </div>
            <div className="container-empty user-settings-fields vertical">
              <InputField
                className="mb-2 "
                labelPosition={"top-left"}
                label={t("Company owner") + ":"}
                value={
                  employerName +
                  " " +
                  employerLastName +
                  " (" +
                  employerID +
                  ")"
                }
                onChange={(e) => {
                  handleInputChange("username", e);
                }}
              ></InputField>
            </div>
          </div>
          <div className="btn-container user-settings-outer">
            <div className="btn-container user-settings-inner">
              <Button
                className={isNarrow ? "" : "ml-3"}
                btnStyle={!leaveDisabled ? "btn-negative" : "btn-disabled"}
                icon={"logout"}
                onClick={showLeaveCompanyPopup}
                tutorialId="user-settings-leave-btn"
              >
                {t("Leave company")}
              </Button>
            </div>
          </div>

          <Title className="mt-4">Permissions</Title>
          <PermissionView />

          <div data-tutorial-id="user-settings-warehouses">
            <WarehousePicker
              className={"w-90 mt-5"}
              selectable={false}
              initWarehouses={allowedWarehouses}
            />
          </div>
          <div className="btn-container w-90 mt-3 mb-4 justify-start">
            <Button
              className={isNarrow ? "" : "ml-3"}
              btnStyle={"btn-negative"}
              icon={"delete"}
              onClick={() => {
                showDeleteAccountPopup();
              }}
              disabled={deleteBtnDisabled}
              tutorialId="user-settings-delete-btn"
            >
              {t("Delete account")}
            </Button>
            <Button
              className={isNarrow ? "" : "ml-3"}
              btnStyle={"btn-positive"}
              icon={"edit"}
              onClick={() => navigate("/settings/packagePage")}
              tutorialId="user-settings-package-btn"
            >
              {t("Change Package")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
